/**
 * ShotGrid — virtualized shot grid for V2 workspace options view.
 * Shows all project shots with selection state, tap, and long-press/double-click preview.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { VirtuosoGrid, type VirtuosoGridHandle } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import { useRegularWidthLayout } from '@/hooks/useRegularWidthLayout.ts';
import { useResponsiveColumns } from '@/hooks/useResponsiveColumns.ts';
import { parseAspectRatio } from '@/utils/aspectRatio.ts';
import { ShotTypeBadge } from '@/components/SceneBoard/ShotTypeBadge';
import type { TimelineShot, TimelineScene } from '@/types/timeline.ts';
import styles from './ShotGrid.module.css';

interface Props {
  shots: TimelineShot[];
  scenes: readonly TimelineScene[];
  separator: string;
  selectedIds: Set<number>;
  videoShotIds?: Set<number>;
  approvedShotIds?: Set<number>;
  onTap: (shotId: number) => void;
  onPreview: (shotId: number, cellEl: HTMLElement) => void;
  onScroll?: () => void;
  scrollParent?: HTMLElement | null;
  /** Shot ID to scroll into view on mount */
  initialScrollToId?: number | null;
}

const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_THRESHOLD = 10;

// Same responsive-column algorithm the board and projects grids run (useResponsiveColumns): a
// minimum tile width, add columns up to a cap, then grow the tiles past the cap instead of adding
// more. Before this the grid was a hard `repeat(6, 1fr)`, so portrait kept six columns in a much
// narrower box and the tiles shrank to ~56px — unusable.
//
// 130 is the landscape-phone tile width, i.e. the size that is already considered acceptable and
// which portrait must therefore never go below. Derived from the actual CSS on an iPhone 14-class
// landscape viewport (844px), which matches useMobileLayout's mobile query in landscape too
// ((pointer: coarse) and (hover: none) and (max-height: 500px)), so the MOBILE branch of every
// value below applies:
//     844  − 2  (.workspace 1px border ×2, AiWorkspace.module.css)
//          − 20 (.main padding 10px ×2, the mobile media query — NOT the clamp's 16px max)
//          − 12 (.gridWrap padding 0 6px ×2 — the padding useResponsiveColumns subtracts)
//        = 810 usable, − 20 (5 gaps × 4px mobile gap) = 790 across 6 tiles → 131.7px each.
// 130 sits just under that, so landscape keeps its six columns (the count only drops once the
// minimum exceeds ~131.6); portrait phone falls to 2 columns of ~176px instead of 6 of ~56px.
export const SHOTGRID_MIN_TILE = 130;
// Must equal .gridList's CSS `gap` for each breakpoint in ShotGrid.module.css — the hook's fit math
// and the grid's actual track math have no shared source of truth (same caveat as the board's and
// projects' copies). useMobileLayout also covers the force-mobile toggle, which the
// `:global(.force-mobile)` gap override in the CSS tracks.
export const SHOTGRID_GAP_MOBILE = 4;
export const SHOTGRID_GAP_DESKTOP = 6;
// Cap kept at the pre-existing fixed count so wide/tablet layouts are unchanged.
export const SHOTGRID_MAX_COLS = 6;

// How many animation frames the late-arrival scroll (effect below) is re-issued for after the
// immediate call. react-virtuoso publishes viewport/item measurements from a ResizeObserver, so a
// scrollToIndex issued in the commit right after mount computes its offset from zero dimensions
// and no-ops — the grid visually stays at the top. Re-issuing is idempotent once measurements
// land (same center target), absorbed while they haven't, and bounded so the loop can never
// fight a live user scroll.
export const INITIAL_SCROLL_REISSUE_FRAMES = 5;

// The wrapper below inherits, verbatim, the sizing VirtuosoGrid's own `style` prop used to carry
// when it was the direct flex child of the tab's `.main`. VirtuosoGrid keeps that style unchanged —
// it just resolves against the wrapper now instead of `.main`.
//   • customScrollParent set (mobile): react-virtuoso gives its root an explicit pixel height equal
//     to the total list height, so this box must stay a plain auto-height block that grows with its
//     content; `.main` (overflow-y: auto) is what scrolls. Constraining the height here would clip.
//   • no customScrollParent (desktop): VirtuosoGrid's root is its own scroller and needs a definite
//     height. flex/min-height/align-self give the wrapper that height, and the flex column makes the
//     wrapper hand the identical flex sizing straight through to the scroller inside it.
const GRID_WRAP_SCROLL_PARENT_STYLE: CSSProperties = { width: '100%' };
const GRID_WRAP_INTERNAL_SCROLL_STYLE: CSSProperties = {
  flex: 1,
  minHeight: 0,
  width: '100%',
  alignSelf: 'stretch',
  display: 'flex',
  flexDirection: 'column',
};

export function ShotGrid({ shots, scenes, separator, selectedIds, videoShotIds, approvedShotIds, onTap, onPreview, onScroll, scrollParent, initialScrollToId }: Props) {
  const { t } = useTranslation('aiWorkspace');
  const isMobile = useMobileLayout();
  const project = useProjectStore(s => s.project);
  const ar = project ? parseAspectRatio(project.aspectRatio) : 16 / 9;
  const aspectStyle = useMemo(
    () => ({ aspectRatio: ar > 0 ? `${ar}` : '16 / 9' }),
    [ar],
  );

  // VirtuosoGrid exposes only an imperative handle, never a DOM ref, so the measured element is an
  // ordinary wrapper around it — the same "measure the outer padded container, apply the resulting
  // --*-cols var to the inner grid element" split the board and projects grids use.
  // Held as state rather than a plain useRef because the wrapper is not always mounted (the empty
  // state returns early below): useResponsiveColumns re-runs its measuring effect on ref identity,
  // so a stable useRef object would leave the effect bailing on a null element with no
  // ResizeObserver ever attached once shots arrived.
  const [gridWrapEl, setGridWrapEl] = useState<HTMLDivElement | null>(null);
  const gridWrapRef = useMemo(() => ({ current: gridWrapEl }), [gridWrapEl]);
  const cols = useResponsiveColumns(gridWrapRef, {
    min: SHOTGRID_MIN_TILE,
    gap: isMobile ? SHOTGRID_GAP_MOBILE : SHOTGRID_GAP_DESKTOP,
    max: SHOTGRID_MAX_COLS,
  });

  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const shotLabel = useCallback((shot: TimelineShot) => {
    const scene = scenes.find(s => s.shots.some(sh => sh.id === shot.id));
    const sceneNum = scene?.number ?? '?';
    return `${sceneNum}${separator}${shot.number || '?'}`;
  }, [scenes, separator]);

  // Owner rule 2026-09-01 (D + E): auto-scroll to the selected shot on tab entry only on
  // regular-width layouts (macOS at a normal window size, iPad .regular); iOS and iPad .compact
  // never scroll — the tiles are under the user's thumb there and a jump on entry loses their
  // place. Read ONCE into state rather than live, because the contract is "when entering": a
  // rotation or Split View resize mid-tab must not retro-fire a scroll the entry never earned.
  // Gating here rather than per tab keeps it one rule for all four ShotGrid mounts.
  const regularWidthLayout = useRegularWidthLayout();
  const [autoScrollAdmitted] = useState(regularWidthLayout);

  const initialIndex = useMemo(() => {
    if (!autoScrollAdmitted || initialScrollToId == null) return undefined;
    const idx = shots.findIndex(s => s.id === initialScrollToId);
    return idx >= 0 ? idx : undefined;
  }, [autoScrollAdmitted, initialScrollToId, shots]);

  // Declared ABOVE itemContent (not next to the effect that reads it) because the cell's onClick
  // closes over it to CONSUME the entry window — see the effect below and its comment block.
  const hasScrolledToInitialRef = useRef(initialIndex != null);

  const itemContent = useCallback((index: number) => {
    const shot = shots[index];
    if (!shot) return null;
    const isSelected = selectedIds.has(shot.id);

    return (
      <div
        className={`${styles.gridCell} ${isSelected ? styles.gridCellSelected : ''}`}
        style={aspectStyle}
        onClick={() => {
          if (longPressFired.current) {
            longPressFired.current = false;
            return;
          }
          // A TAP ENDS THE ENTRY WINDOW (2026-09-01, final-review item 4). The late-arrival effect
          // below only exists for useAiWorkspaceFlow's post-mount pre-selection, which always
          // precedes any gesture; but a grid that mounted with no target at all (Enhance deselect →
          // tab switch writes an empty selection, or an open with nothing at the playhead) leaves
          // the flag UNCONSUMED, and this very tap is what resolves initialScrollToId null→id. The
          // effect would then re-centre the grid ON A TAP, which the "when entering" contract
          // forbids. Consume before onTap, since onTap is what moves the prop.
          hasScrolledToInitialRef.current = true;
          onTap(shot.id);
        }}
        onDoubleClick={e => {
          onPreview(shot.id, e.currentTarget as HTMLElement);
        }}
        onTouchStart={e => {
          longPressFired.current = false;
          const touch = e.touches[0];
          touchStart.current = { x: touch.clientX, y: touch.clientY };
          const el = e.currentTarget as HTMLElement;
          longPressTimer.current = setTimeout(() => {
            longPressFired.current = true;
            longPressTimer.current = null;
            onPreview(shot.id, el);
          }, LONG_PRESS_MS);
        }}
        onTouchMove={e => {
          if (!touchStart.current) return;
          const touch = e.touches[0];
          const dx = touch.clientX - touchStart.current.x;
          const dy = touch.clientY - touchStart.current.y;
          if (Math.sqrt(dx * dx + dy * dy) > LONG_PRESS_MOVE_THRESHOLD) {
            cancelLongPress();
          }
        }}
        onTouchEnd={cancelLongPress}
        onTouchCancel={cancelLongPress}
      >
        {shot.thumbnailDataUrl ? (
          <img className={styles.cellThumb} src={shot.thumbnailDataUrl} alt="" />
        ) : (
          <div className={styles.cellPending}>{shot.number || '?'}</div>
        )}
        <div className={styles.labelStrip}>{shotLabel(shot)}</div>
        <ShotTypeBadge shot={shot} size="compact" hasVideo={videoShotIds?.has(shot.id)} />
        {approvedShotIds?.has(shot.id) && (
          <div className={styles.approvedBadge} />
        )}
      </div>
    );
  }, [shots, selectedIds, videoShotIds, approvedShotIds, aspectStyle, onTap, onPreview, cancelLongPress, shotLabel]);

  // Imperative scroll-to-selected: initialTopMostItemIndex only applies at mount, but
  // the pre-selected shot id can arrive via a post-mount effect (e.g. AI-workspace
  // entry from the board — useAiWorkspaceFlow.ts sets it a tick after ShotGrid mounts),
  // which loses the race against that mount-only prop. This effect covers ONLY that
  // late-arriving case: when the id is already resolvable on the mount render, the
  // initialTopMostItemIndex prop (passed below) positions the grid and the one-shot
  // flag starts consumed (lazy useRef init runs once, on mount) so the effect doesn't
  // follow up with a visible re-center jump. An id that is set but not yet found in
  // `shots` leaves initialIndex undefined at mount, so the flag stays unconsumed and
  // the effect still fires once the shot appears. The guard also ensures at most one
  // fire per mount — later user taps that change selectedIds (and can transiently
  // null/re-resolve initialScrollToId, see StyleGradeTab's `selectedShotIds.size === 1`
  // gate) must never re-trigger a scroll.
  // The mechanism below is unchanged by the compact gate above: on a compact layout initialIndex
  // is undefined for the whole mount, so the flag starts unconsumed and this effect returns early
  // every time — neither path can fire.
  // The flag has a SECOND writer since 2026-09-01: the cell's own onClick consumes it, because a
  // grid that mounted with initialScrollToId == null starts UNCONSUMED and the first tap is what
  // resolves the prop — see that consume for the whole argument. (The ref and the initialIndex memo
  // it seeds from are declared above itemContent for exactly that reason.)
  const virtuosoRef = useRef<VirtuosoGridHandle>(null);
  useEffect(() => {
    if (hasScrolledToInitialRef.current || initialIndex == null) return;
    hasScrolledToInitialRef.current = true;
    const scroll = () => virtuosoRef.current?.scrollToIndex({ index: initialIndex, align: 'center' });
    scroll();
    // A single call can land before virtuoso's ResizeObserver has published measurements (they
    // start at zero), computing a zero offset and no-opping — so re-issue for a few frames.
    // See INITIAL_SCROLL_REISSUE_FRAMES for why this is safe.
    let framesLeft = INITIAL_SCROLL_REISSUE_FRAMES;
    let rafId: number | null = null;
    const reissue = () => {
      rafId = null;
      scroll();
      framesLeft -= 1;
      if (framesLeft > 0) rafId = requestAnimationFrame(reissue);
    };
    rafId = requestAnimationFrame(reissue);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [initialIndex]);

  if (shots.length === 0) {
    return <div className={styles.empty}>{t('noShotsInProject')}</div>;
  }

  return (
    <div
      ref={setGridWrapEl}
      className={styles.gridWrap}
      style={{
        ...(scrollParent ? GRID_WRAP_SCROLL_PARENT_STYLE : GRID_WRAP_INTERNAL_SCROLL_STYLE),
        ['--shotgrid-cols']: cols,
      } as CSSProperties}
    >
      <VirtuosoGrid
        ref={virtuosoRef}
        totalCount={shots.length}
        itemContent={itemContent}
        listClassName={styles.gridList}
        itemClassName={styles.gridItem}
        overscan={isMobile ? { main: 200, reverse: 200 } : { main: 600, reverse: 600 }}
        style={scrollParent ? { width: '100%' } : { flex: 1, minHeight: 0, width: '100%', alignSelf: 'stretch' }}
        isScrolling={scrolling => { if (scrolling && onScroll) onScroll(); }}
        {...(scrollParent ? { customScrollParent: scrollParent } : {})}
        {...(initialIndex != null ? { initialTopMostItemIndex: initialIndex } : {})}
      />
    </div>
  );
}
